import { useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import useIsDirty from '../../../../shared/useIsDirty'
import { askDiscardChanges } from '../../../../shared/confirmDialog'

import Btn from '../../../../UI/Btn'
import { PlainInput, PlainTextarea } from '../../../../UI/PlainField'

import laufzeitText from './projectRuntime'
import styles from './projects.module.scss'

const EMPTY = {
	title: '',
	shortTitle: '',
	description: '',
	startDate: '',
	endDate: '',
	isDefaultForRequests: false
}

// Dieselbe Grenze wie in der Datenbank (`TicketProjects.shortTitle`, STRING(5)).
const KURZ_MAX = 5

/**
 * Projekte: Bündel von Anforderungen mit Titel und Laufzeit.
 *
 * Beim Löschen bleiben die Anforderungen bestehen, nur die Zuordnung fällt
 * weg. Das steht auch in der Rückfrage — sonst müsste man raten, ob ein
 * gelöschtes Projekt seine Tickets mitnimmt.
 */
export default function ProjectsModal({ projects, onClose, onChanged }) {
	const [form, setForm] = useState(EMPTY)
	const [editingId, setEditingId] = useState(null)
	const [isSaving, setSaving] = useState(false)

	// Angefangenes Projekt im Erfassungsteil des Fensters — die Liste darunter
	// wird sofort gespeichert, da gibt es nichts zu verlieren.
	const isDirty = useIsDirty(form)

	const schliessen = async () => {
		if (isDirty && !(await askDiscardChanges())) return

		onClose()
	}

	const backdropProps = useDialogDismiss(schliessen, { enabled: !isSaving })

	const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

	// Beide Daten sind freiwillig; nur zusammen müssen sie in der Reihenfolge
	// stehen. Der Vergleich geht auf den Zeichenketten (JJJJ-MM-TT) auf.
	const verdrehteLaufzeit = Boolean(form.startDate && form.endDate && form.endDate < form.startDate)

	const reset = () => {
		setForm(EMPTY)
		setEditingId(null)
	}

	const submit = event => {
		event.preventDefault()
		if (form.title.trim().length < 3) return
		// Ohne Kürzel stünde in der Anforderungsliste wieder der volle Titel —
		// der Server lehnt das Anlegen ohnehin ab, hier bleibt es ohne Umweg.
		if (!form.shortTitle.trim()) return
		// Der Server lehnt das auch ab, aber erst nach dem Abschicken und mit
		// einer Meldung, die vorne „Validation error" stehen hat.
		if (verdrehteLaufzeit) {
			toast.error('Das Ende darf nicht vor dem Beginn liegen.')

			return
		}

		setSaving(true)
		const payload = {
			title: form.title.trim(),
			shortTitle: form.shortTitle.trim(),
			description: form.description.trim() || null,
			startDate: form.startDate || null,
			endDate: form.endDate || null,
			isDefaultForRequests: form.isDefaultForRequests
		}

		// `disableToast`: Der eigene Satz sagt genauer, was nicht geklappt hat.
		const request = editingId
			? http.put(TApi.ADMIN_TICKET_PROJECT_ID(editingId), payload, { disableToast: true })
			: http.post(TApi.ADMIN_TICKET_PROJECTS, payload, { disableToast: true })

		request
			.then(() => {
				toast.success(editingId ? 'Projekt gespeichert.' : 'Projekt angelegt.')
				reset()
				onChanged()
			})
			.catch(error => notifyError(error, 'Speichern fehlgeschlagen.'))
			.finally(() => setSaving(false))
	}

	const remove = project => {
		// eslint-disable-next-line no-alert
		if (
			!window.confirm(
				`Projekt „${project.title}“ löschen? Die zugeordneten Anforderungen bleiben erhalten und verlieren nur die Zuordnung.`
			)
		)
			return

		http.delete(TApi.ADMIN_TICKET_PROJECT_ID(project.id), { disableToast: true })
			.then(() => {
				toast.success('Projekt gelöscht.')
				if (editingId === project.id) reset()
				onChanged()
			})
			.catch(error => notifyError(error, 'Das Projekt konnte nicht gelöscht werden.'))
	}

	return (
		<div className={styles.backdrop} {...backdropProps}>
			<div className={styles.modal}>
				<header className={styles.head}>
					<h2 className={styles.title}>Projekte</h2>
					<button type="button" className={styles.close} onClick={schliessen} aria-label="Schließen">
						×
					</button>
				</header>

				<div className={styles.body}>
					<form className={styles.form} onSubmit={submit}>
						<h3 className={styles.formTitle}>{editingId ? 'Projekt bearbeiten' : 'Neues Projekt'}</h3>
						<div className={styles.formGrid}>
							<PlainInput
								label="Titel"
								value={form.title}
								maxLength={255}
								onChange={event => set('title', event.target.value)}
							/>
							{/*
							 * Das Kürzel steht in der Anforderungsliste anstelle des
							 * Titels. Fünf Zeichen sind knapp gewählt: Bei zehn wären
							 * es wieder halbe Wörter und die Spalte bliebe breit.
							 */}
							<PlainInput
								label="Kurzbezeichnung"
								value={form.shortTitle}
								maxLength={KURZ_MAX}
								required
								placeholder="z. B. S4R"
								onChange={event => set('shortTitle', event.target.value)}
							/>
							<PlainInput
								label="Beginn"
								type="date"
								value={form.startDate || ''}
								onChange={event => set('startDate', event.target.value)}
							/>
							<PlainInput
								label="Ende"
								type="date"
								value={form.endDate || ''}
								error={verdrehteLaufzeit ? 'Vor dem Beginn.' : undefined}
								onChange={event => set('endDate', event.target.value)}
							/>
						</div>
						<p className={styles.hint}>
							Die Kurzbezeichnung steht in der Anforderungsliste in der Spalte „Projekt" — höchstens{' '}
							{KURZ_MAX} Zeichen, und jede nur einmal. Beginn und Ende umreißen die Laufzeit; beides darf
							offen bleiben.
						</p>
						{/*
						 * Anfragen über den Hilfe-Knopf kommen täglich herein und
						 * mussten bisher einzeln nachgebucht werden. Der Haken sagt,
						 * wohin sie von selbst laufen — in aller Regel die laufende
						 * Wartung.
						 *
						 * Am Projekt und nicht als Einstellung mit fester Nummer: Das
						 * Projekt heißt „… 2025/26" und ist im nächsten Schuljahr ein
						 * anderes. Wer dann das neue anlegt, setzt den Haken dort.
						 */}
						<label className={styles.check}>
							<input
								type="checkbox"
								checked={form.isDefaultForRequests}
								onChange={event => set('isDefaultForRequests', event.target.checked)}
							/>
							<span>
								Neue Anfragen hier buchen
								<span className={styles.checkHint}>
									Anfragen über den Hilfe-Knopf laufen ohne Zutun auf dieses Projekt. Den Haken trägt
									immer nur ein Projekt — ihn hier zu setzen nimmt ihn dem bisherigen weg.
								</span>
							</span>
						</label>
						<PlainTextarea
							label="Beschreibung"
							rows={2}
							value={form.description}
							onChange={event => set('description', event.target.value)}
						/>
						<div className={styles.formActions}>
							{editingId && (
								<Btn type="button" size="small" theme="secondary" onClick={reset}>
									Abbrechen
								</Btn>
							)}
							<Btn
								type="submit"
								size="small"
								theme="primary"
								disabled={
									isSaving || !form.title.trim() || !form.shortTitle.trim() || verdrehteLaufzeit
								}
							>
								{editingId ? 'Speichern' : 'Anlegen'}
							</Btn>
						</div>
					</form>

					{!projects.length ? (
						<p className={styles.empty}>Noch keine Projekte angelegt.</p>
					) : (
						<ul className={styles.list}>
							{projects.map(project => (
								<li key={project.id} className={styles.item}>
									<div className={styles.itemMain}>
										<span className={styles.itemTitle}>
											{project.shortTitle && (
												<span className={styles.itemShort}>{project.shortTitle}</span>
											)}
											{project.title}
											{project.isDefaultForRequests && (
												<span
													className={styles.itemDefault}
													title="Anfragen über den Hilfe-Knopf laufen auf dieses Projekt."
												>
													Anfragen
												</span>
											)}
										</span>
										{project.description && (
											<span className={styles.itemDesc}>{project.description}</span>
										)}
									</div>
									<span className={styles.itemMeta}>
										{laufzeitText(project) || 'ohne Laufzeit'}
										{typeof project.ticketCount === 'number' &&
											` · ${project.ticketCount} Anforderung${
												project.ticketCount === 1 ? '' : 'en'
											}`}
									</span>
									<div className={styles.itemActions}>
										<button
											type="button"
											className={styles.link}
											onClick={() => {
												setEditingId(project.id)
												setForm({
													title: project.title,
													shortTitle: project.shortTitle || '',
													description: project.description || '',
													startDate: project.startDate || '',
													endDate: project.endDate || '',
													isDefaultForRequests: Boolean(project.isDefaultForRequests)
												})
											}}
										>
											Bearbeiten
										</button>
										<button
											type="button"
											className={styles.linkDanger}
											onClick={() => remove(project)}
										>
											Löschen
										</button>
									</div>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>
	)
}

ProjectsModal.propTypes = {
	projects: PropTypes.array.isRequired,
	onClose: PropTypes.func.isRequired,
	onChanged: PropTypes.func.isRequired
}
